import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '../api'
import { useUserStore } from './userStore'
import { useBandStore } from './bandStore'
import { usePresenceStore } from './presenceStore'
import type { SongDto, CreateSongDto, CreateSongPartDto, SongSummaryDto, CreateMixDto, UpdateSongDto, UpdateSongPartDto, SongPartLogDto, ChordDto } from '../dtos'

export const useSongStore = defineStore('song', () => {
    const userStore = useUserStore()
    const bandStore = useBandStore()
    const presenceStore = usePresenceStore()
    
    const songs = ref<SongSummaryDto[]>([])
    const currentSong = ref<SongDto | null>(null)
    const loading = ref(false)
    const detailLoading = ref(false)
    const partsLoading = ref(false)
    const isFetching = ref(false)

    async function fetchBandSongs(bandId: number) {
        if (!userStore.user || isFetching.value) return
        
        loading.value = true
        isFetching.value = true
        songs.value = [] // Clear existing songs immediately
        try {
            const token = await userStore.user.getIdToken()
            const response = await api.get(`/songs/band/${bandId}`, {
                headers: { Authorization: `Bearer ${token}` }
            })
            songs.value = response.data
        } catch (error) {
            console.error('Failed to fetch songs:', error)
        } finally {
            loading.value = false
            isFetching.value = false
        }
    }

    async function fetchSongDetails(songId: number) {
        if (!userStore.user) return
        detailLoading.value = true
        try {
            const token = await userStore.user.getIdToken()
            const response = await api.get(`/songs/${songId}`, {
                headers: { Authorization: `Bearer ${token}` }
            })
            // Ensure parts and mixes are never undefined
            const data = response.data
            if (!data.parts) data.parts = []
            if (!data.mixes) data.mixes = []
            currentSong.value = data
            
            presenceStore.startPresence('Song', songId)
        } catch (error) {
            console.error('Failed to fetch song details:', error)
        } finally {
            detailLoading.value = false
        }
    }

    async function addSong(title: string, opts?: { artist?: string, key?: string, bPM?: number, notes?: string, tuning?: string, capo?: number, chords?: ChordDto[] }) {
        if (!userStore.user || !bandStore.currentBand) return
        try {
            const token = await userStore.user.getIdToken()
            const payload: CreateSongDto = {
                title,
                bandId: bandStore.currentBand.id,
                ...opts
            }
            const response = await api.post('/songs', payload, {
                headers: { Authorization: `Bearer ${token}` }
            })
            const summary: SongSummaryDto = {
                id: response.data.id,
                title: response.data.title,
                artist: response.data.artist,
                bandId: response.data.bandId,
                updatedAt: response.data.updatedAt,
                mixCount: 0,
                rowVersion: response.data.rowVersion
            }
            songs.value.push(summary)
            songs.value.sort((a, b) => a.title.localeCompare(b.title))
            return response.data
        } catch (error) {
            console.error('Failed to add song:', error)
            throw error
        }
    }

    async function importSong(file: File) {
        if (!userStore.user || !bandStore.currentBand) return

        const imageTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
        let data: any
        if (imageTypes.includes(file.type)) {
            const token = await userStore.user.getIdToken()
            const formData = new FormData()
            formData.append('file', file)
            if (bandStore.currentBand?.id) formData.append('bandId', String(bandStore.currentBand.id))
            const response = await api.post('/songs/parse-image', formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            })
            data = response.data
        } else {
            const text = await file.text()
            data = JSON.parse(text)
        }

        if (!data.title) {
            data.title = file.name.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' ')
        }

        const chords: ChordDto[] | undefined = data.chords?.length
            ? data.chords.filter((c: any) => c.name && c.voicing).map((c: { name: string, voicing: string }) => ({ name: c.name, voicing: c.voicing }))
            : undefined

        const song = await addSong(data.title, {
            artist: data.artist || undefined,
            key: data.key || undefined,
            bPM: data.bpm ? Number(data.bpm) : undefined,
            notes: data.notes || undefined,
            tuning: data.tuning || undefined,
            capo: data.capo != null ? Number(data.capo) : undefined,
            chords
        })
        if (!song) return

        const token = await userStore.user!.getIdToken()
        for (const part of data.parts || []) {
            const partPayload: CreateSongPartDto = {
                name: part.name,
                songId: song.id,
                sequenceNumber: part.sequenceNumber,
                performance: part.performance || undefined
            }
            await api.post('/songparts', partPayload, {
                headers: { Authorization: `Bearer ${token}` }
            })
        }

        return song
    }

    async function updateSong(songId: number, data: Omit<UpdateSongDto, 'rowVersion'>) {
        if (!userStore.user || !currentSong.value) return
        try {
            const token = await userStore.user.getIdToken()
            const payload: UpdateSongDto = {
                ...data,
                rowVersion: currentSong.value.rowVersion
            }
            const response = await api.put(`/songs/${songId}`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            })
            // Update in local lists
            const sIndex = songs.value.findIndex(s => s.id === songId)
            if (sIndex !== -1 && songs.value[sIndex]) {
                songs.value[sIndex]!.title = response.data.title
                songs.value[sIndex]!.artist = response.data.artist
                songs.value[sIndex]!.rowVersion = response.data.rowVersion
            }
            if (currentSong.value && currentSong.value.id === songId) {
                const parts = currentSong.value.parts || []
                const mixes = currentSong.value.mixes || []
                currentSong.value = response.data
                if (currentSong.value) {
                    if (!currentSong.value.parts || currentSong.value.parts.length === 0) currentSong.value.parts = parts
                    if (!currentSong.value.mixes || currentSong.value.mixes.length === 0) currentSong.value.mixes = mixes
                }
            }
            return response.data
        } catch (error) {
            console.error('Failed to update song:', error)
            throw error
        }
    }

    async function addSongPart(name: string, songId: number) {
        if (!userStore.user || !currentSong.value) return
        try {
            const token = await userStore.user.getIdToken()
            const payload: CreateSongPartDto = {
                name,
                songId,
                sequenceNumber: (currentSong.value.parts?.length || 0) + 1
            }
            const response = await api.post('/songparts', payload, {
                headers: { Authorization: `Bearer ${token}` }
            })
            if (currentSong.value && currentSong.value.id === songId) {
                if (!currentSong.value.parts) currentSong.value.parts = []
                currentSong.value.parts.push(response.data)
            }
            return response.data
        } catch (error) {
            console.error('Failed to add song part:', error)
            throw error
        }
    }

    async function toggleSongPartActive(partId: number) {
        if (!userStore.user || !currentSong.value) return
        const part = currentSong.value.parts.find(p => p.id === partId)
        if (!part) return
        return updateSongPart(partId, { isActive: !part.isActive })
    }

    async function updateSongPart(partId: number, data: { name?: string, performance?: string, sequenceNumber?: number, isActive?: boolean }) {
        if (!userStore.user || !currentSong.value) return
        try {
            const part = currentSong.value.parts.find(p => p.id === partId)
            if (!part) return

            const token = await userStore.user.getIdToken()
            const payload: UpdateSongPartDto = {
                ...data,
                rowVersion: part.rowVersion
            }
            const response = await api.put(`/songparts/${partId}`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            })
            if (currentSong.value) {
                const pIndex = currentSong.value.parts.findIndex(p => p.id === partId)
                if (pIndex !== -1) currentSong.value.parts[pIndex] = response.data
            }
            return response.data
        } catch (error) {
            console.error('Failed to update song part:', error)
            throw error
        }
    }

    async function duplicateSongPart(partId: number) {
        if (!userStore.user || !currentSong.value) return
        try {
            const token = await userStore.user.getIdToken()
            const response = await api.post(`/songparts/${partId}/duplicate`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            })
            if (currentSong.value) {
                currentSong.value.parts.push(response.data)
            }
            return response.data
        } catch (error) {
            console.error('Failed to duplicate song part:', error)
            throw error
        }
    }

    async function reorderSongParts(songId: number, partIds: number[]) {
        if (!userStore.user) return
        try {
            const token = await userStore.user.getIdToken()
            await api.post(`/songparts/song/${songId}/reorder`, partIds, {
                headers: { Authorization: `Bearer ${token}` }
            })
            if (currentSong.value && currentSong.value.id === songId) {
                partIds.forEach((id, index) => {
                    const part = currentSong.value?.parts.find(p => p.id === id)
                    if (part) part.sequenceNumber = index + 1
                })
                currentSong.value.parts.sort((a, b) => a.sequenceNumber - b.sequenceNumber)
            }
        } catch (error) {
            console.error('Failed to reorder parts:', error)
            throw error
        }
    }

    async function fetchSongPartLogs(partId: number): Promise<SongPartLogDto[]> {
        if (!userStore.user) return []
        try {
            const token = await userStore.user.getIdToken()
            const response = await api.get(`/songparts/${partId}/logs`, {
                headers: { Authorization: `Bearer ${token}` }
            })
            return response.data
        } catch (error) {
            console.error('Failed to fetch song part logs:', error)
            return []
        }
    }

    async function duplicateSong(songId: number) {
        if (!userStore.user || !bandStore.currentBand) return
        try {
            const token = await userStore.user.getIdToken()
            const response = await api.post(`/songs/${songId}/duplicate`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            })
            const summary: SongSummaryDto = {
                id: response.data.id,
                title: response.data.title,
                artist: response.data.artist,
                bandId: response.data.bandId,
                updatedAt: response.data.updatedAt,
                mixCount: 0,
                rowVersion: response.data.rowVersion
            }
            songs.value.push(summary)
            songs.value.sort((a, b) => a.title.localeCompare(b.title))
            return response.data
        } catch (error) {
            console.error('Failed to duplicate song:', error)
            throw error
        }
    }

    async function deleteSong(songId: number) {
        if (!userStore.user) return
        try {
            const token = await userStore.user.getIdToken()
            await api.delete(`/songs/${songId}`, {
                headers: { Authorization: `Bearer ${token}` }
            })
            songs.value = songs.value.filter(s => s.id !== songId)
        } catch (error) {
            console.error('Failed to delete song:', error)
            throw error
        }
    }

    async function deleteSongPart(partId: number) {
        if (!userStore.user || !currentSong.value) return
        try {
            const token = await userStore.user.getIdToken()
            await api.delete(`/songparts/${partId}`, {
                headers: { Authorization: `Bearer ${token}` }
            })
            if (currentSong.value) {
                currentSong.value.parts = currentSong.value.parts.filter(p => p.id !== partId)
            }
        } catch (error) {
            console.error('Failed to delete song part:', error)
            throw error
        }
    }

    async function addMix(title: string, songId: number) {
        if (!userStore.user || !currentSong.value) return
        try {
            const token = await userStore.user.getIdToken()
            const payload: CreateMixDto = { title, songId }
            const response = await api.post('/mixes', payload, {
                headers: { Authorization: `Bearer ${token}` }
            })
            if (currentSong.value && currentSong.value.id === songId) {
                if (!currentSong.value.mixes) currentSong.value.mixes = []
                currentSong.value.mixes.unshift(response.data)
                
                // Update mixCount in songs list
                const song = songs.value.find(s => s.id === songId)
                if (song) song.mixCount++
            }
            return response.data
        } catch (error) {
            console.error('Failed to add mix:', error)
            throw error
        }
    }

    async function deleteMix(mixId: number) {
        if (!userStore.user || !currentSong.value) return
        try {
            const token = await userStore.user.getIdToken()
            await api.delete(`/mixes/${mixId}`, {
                headers: { Authorization: `Bearer ${token}` }
            })
            if (currentSong.value) {
                const songId = currentSong.value.id
                currentSong.value.mixes = currentSong.value.mixes.filter(m => m.id !== mixId)
                
                // Update mixCount in songs list
                const song = songs.value.find(s => s.id === songId)
                if (song && song.mixCount > 0) song.mixCount--
            }
        } catch (error) {
            console.error('Failed to delete mix:', error)
            throw error
        }
    }

    function clear() {
        songs.value = []
        currentSong.value = null
        loading.value = false
        detailLoading.value = false
        partsLoading.value = false
        isFetching.value = false
        presenceStore.stopPresence()
    }

    return { 
        songs, loading, detailLoading, partsLoading, isFetching, currentSong, 
        fetchBandSongs, fetchSongDetails, addSong, importSong, updateSong, deleteSong,
        addSongPart, updateSongPart, toggleSongPartActive, duplicateSongPart, deleteSongPart, reorderSongParts, fetchSongPartLogs, duplicateSong,
        addMix, deleteMix, clear 
    }
})
